import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Coins, Gift } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtNum } from "@/lib/format";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1607083206968-13611e3d76db?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";
const empty = { name: "", description: "", category: "Experiencias", points_required: 1000, image: "", stock: 10, active: true };

export default function AdminRewards() {
  usePageTitle("Catálogo de premios");
  const [rewards, setRewards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(empty);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<"active" | "inactive" | "all">("active");
  const [toDelete, setToDelete] = useState<any>(null);

  // /admin/rewards incluye los inactivos (el /rewards público los oculta).
  const load = () => {
    setFailed(false);
    return api.get("/admin/rewards").then((r) => setRewards(r.data)).catch(() => setFailed(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const inactiveCount = rewards.filter((r) => !r.active).length;
  const shown = rewards.filter((r) => view === "all" || (view === "active" ? r.active : !r.active));
  const categories = Array.from(new Set(rewards.map((r) => r.category).filter(Boolean))).sort();

  const openNew = () => { setEditing("new"); setForm(empty); setErrors({}); };
  const openEdit = (r: any) => { setEditing(r.id); setForm({ ...r }); setErrors({}); };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name?.trim()) e.name = "Escribe el nombre del premio.";
    if (!form.category?.trim()) e.category = "Elige o escribe una categoría.";
    const pts = Number(form.points_required);
    if (!Number.isInteger(pts) || pts < 1) e.points_required = "Los puntos deben ser un número entero mayor que 0.";
    const stock = Number(form.stock);
    if (!Number.isInteger(stock) || stock < 0) e.stock = "Las unidades deben ser un número entero, 0 o más.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim(), category: form.category.trim(), points_required: Number(form.points_required), stock: Number(form.stock) };
      if (editing === "new") await api.post("/admin/rewards", payload);
      else await api.put(`/admin/rewards/${editing}`, payload);
      toast.success(payload.active ? "Premio guardado. Los clientes ya lo ven en el catálogo." : "Premio guardado como inactivo: los clientes no lo ven.");
      setEditing(null); load();
    } catch (err: any) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/rewards/${toDelete.id}`);
      toast.success(`“${toDelete.name}” se eliminó del catálogo`);
      load();
    } catch (err: any) { toast.error(apiError(err)); throw err; }
  };

  const fieldErr = (k: string) => errors[k] && <p id={`reward-${k}-error`} className="text-xs text-red-700 mt-1" role="alert">{errors[k]}</p>;
  const invalid = (k: string) => (errors[k] ? { "aria-invalid": true, "aria-describedby": `reward-${k}-error` } : {});

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-rewards-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-medium">Catálogo de premios</h1>
          <p className="text-sm text-slate-600 mt-1">Los premios que los clientes pueden canjear con sus puntos.</p>
        </div>
        <Button data-testid="new-reward-button" onClick={openNew} className="gold-gradient-bg text-white font-semibold rounded-full">
          <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nueva recompensa
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { key: "active", label: "Activos" },
          { key: "inactive", label: `Inactivos${inactiveCount ? ` (${inactiveCount})` : ""}` },
          { key: "all", label: "Todos" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`reward-tab-${t.key}`}
            onClick={() => setView(t.key as any)}
            aria-pressed={view === t.key}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
              view === t.key ? "gold-gradient-bg text-white font-semibold border-transparent" : "border-slate-200 text-slate-600 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {failed ? (
        <LoadError what="el catálogo" onRetry={load} />
      ) : shown.length === 0 ? (
        <EmptyState icon={Gift}>
          {view === "inactive" ? "No hay premios inactivos." : <>Aún no hay premios en esta lista. Crea uno con “Nueva recompensa”.</>}
        </EmptyState>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((r) => (
            <div key={r.id} data-testid="admin-reward-card" className={`card-obsidian overflow-hidden ${r.active ? "" : "opacity-80"}`}>
              <img src={r.image || FALLBACK_IMG} alt="" onError={(e: any) => (e.currentTarget.src = FALLBACK_IMG)} className="w-full h-36 object-cover" />
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-semibold text-slate-900">{r.name}</h2>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0">{r.category}</span>
                </div>
                {!r.active && (
                  <p data-testid="reward-inactive-badge" className="mt-2 text-xs font-medium text-slate-700 bg-slate-100 border border-slate-200 rounded-full inline-block px-2 py-0.5">
                    Inactivo · los clientes no lo ven
                  </p>
                )}
                <div className="flex items-center gap-3 mt-2 text-sm">
                  <span className="text-gold-ink font-mono flex items-center gap-1"><Coins className="w-3.5 h-3.5" aria-hidden="true" />{fmtNum(r.points_required)} pts</span>
                  <span className={r.stock > 0 ? "text-slate-600" : "text-red-700 font-medium"}>{r.stock > 0 ? `${fmtNum(r.stock)} disponibles` : "Agotado"}</span>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-100 h-9">
                    <Pencil className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Editar
                  </Button>
                  <Button data-testid="delete-reward-button" size="sm" variant="outline" onClick={() => setToDelete(r)} aria-label={`Eliminar ${r.name}`} className="border-red-500/30 text-red-700 hover:bg-red-500/10 h-9">
                    <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o: boolean) => !o && setEditing(null)}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing === "new" ? "Nueva recompensa" : "Editar recompensa"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reward-name" className="text-slate-700 text-sm">Nombre *</Label>
              <Input id="reward-name" data-testid="reward-name-input" value={form.name} onChange={(e: any) => setForm({ ...form, name: e.target.value })} {...invalid("name")} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
              {fieldErr("name")}
            </div>
            <div>
              <Label htmlFor="reward-description" className="text-slate-700 text-sm">Descripción (la ven los clientes)</Label>
              <Textarea id="reward-description" value={form.description} onChange={(e: any) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="reward-category" className="text-slate-700 text-sm">Categoría *</Label>
                <Input id="reward-category" list="reward-categories" value={form.category} onChange={(e: any) => setForm({ ...form, category: e.target.value })} {...invalid("category")} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
                <datalist id="reward-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
                {fieldErr("category")}
              </div>
              <div>
                <Label htmlFor="reward-points" className="text-slate-700 text-sm">Puntos para canjearlo *</Label>
                <Input id="reward-points" data-testid="reward-points-input" type="number" min={1} value={form.points_required} onChange={(e: any) => setForm({ ...form, points_required: e.target.value })} {...invalid("points_required")} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
                {fieldErr("points_required")}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
              <div>
                <Label htmlFor="reward-stock" className="text-slate-700 text-sm">Unidades disponibles *</Label>
                <Input id="reward-stock" type="number" min={0} value={form.stock} onChange={(e: any) => setForm({ ...form, stock: e.target.value })} {...invalid("stock")} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
                {fieldErr("stock")}
              </div>
              <div className="pt-7">
                <div className="flex items-center gap-2">
                  <Switch id="reward-active" checked={form.active} onCheckedChange={(v: boolean) => setForm({ ...form, active: v })} />
                  <Label htmlFor="reward-active" className="text-sm text-slate-700">Visible para los clientes</Label>
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="reward-image" className="text-slate-700 text-sm">Enlace de la imagen</Label>
              <Input id="reward-image" value={form.image} onChange={(e: any) => setForm({ ...form, image: e.target.value })} placeholder="https://..." className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
              <p className="text-xs text-slate-600 mt-1">Pega la dirección de una imagen publicada en internet. Si no carga, se usa una imagen genérica.</p>
              {form.image && <img src={form.image} alt="Vista previa" onError={(e: any) => (e.currentTarget.src = FALLBACK_IMG)} className="mt-2 w-full h-28 object-cover rounded-lg border border-slate-200" />}
            </div>
            <p className="text-xs text-slate-600">* Campos obligatorios</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
            <Button data-testid="save-reward-button" onClick={save} disabled={saving} className="gold-gradient-bg text-white font-semibold rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        testid="delete-reward-dialog"
        danger
        title={`¿Eliminar “${toDelete?.name || ""}”?`}
        description={<>
          <p>El premio desaparecerá del catálogo y del panel. Esta acción no se puede deshacer.</p>
          <p>Si solo quieres que los clientes dejen de verlo, edítalo y desactiva “Visible para los clientes”.</p>
        </>}
        confirmLabel="Eliminar premio"
        onConfirm={remove}
      />
    </div>
  );
}
